import React, {useState} from 'react';
import propTypes from 'prop-types';
import {gql} from '@apollo/client';
import {get, findIndex} from 'lodash';
import {Grid, Box} from '@mui/material';
import styled from '@emotion/styled';
import produce from 'immer';
import {ReactSVG} from 'react-svg';
import qs from 'qs';

import client from '../../src/graphql/client';
import {theme} from '../../src/themes/default-theme';

// queries
import respImgQuery from '../../src/graphql/queries/resp-img';
import seoQuery from '../../src/graphql/queries/seo';
import contentBuilderQuery from '../../src/graphql/queries/content-builder';

// data restructor
import refactorBreadcrumbsData from '../../src/data-restructure/navigation/breadcrumbs/breadcrumbs';
import restructorBrochures from '../../src/data-restructure/pages/brochure-request';
import refactorHb from '../../src/data-restructure/multimedia/hero-banner';
import refactorHBCData from '../../src/data-restructure/multimedia/hero-banner-carousel';

// components
import Banner from '../../src/components/multimedia/Banner';
import BannerSlider from '../../src/components/multimedia/BannerSlider';
import Text from '../../src/components/typography';
import RespImg from '../../src/components/ui/RespImg';
import BannerPopup from '../../src/components/ui/BannerPopup';

import bannerAsset from '../../src/assets/brochure-pattern.png';
import tickWhite from '../../src/assets/icons/tick.svg';
import { Cta } from '../../src/components/ui';


const Container = styled.div`
  background-color: #F3F4F7;
  min-height: 80vh;
`

const TopBanner = styled.div`
  width: 100%;
  height: 56px;
  display: block;
  background-image: url(${bannerAsset});
  background-size: 1600px;
  background-position: bottom;
`

const Section = styled.div`
  margin: 40px 0 0;
  padding-bottom: 20px;

  ${theme.breakpoints.up('md')} {
    margin: 80px 0 0;
    padding-bottom: 60px;

  }
  
  ${theme.breakpoints.up('lg')} {
    margin: 115px 0 0;
    padding-bottom: 100px;

  }
`

const TitleContainer = styled.div`
  margin-bottom: 30px;

  h2 {
    color: #2E3238;
    font-size: 20px;
    font-style: normal;
    line-height: 24px;
    margin-top: 35px;
    margin-bottom: 16px;
  }
`

const Brochures = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1394px;
  margin: 0 auto;
  margin-top: 51px;

  ${theme.breakpoints.down('md')} {
    padding-left: 20px;
    padding-right: 20px;
  }
`

const BrochureItem = styled.div`
  width: 320px;
  padding-left: 14px;
  padding-right: 14px;
  margin-bottom: 26px;

  ${theme.breakpoints.only('md')} {
    width: 30%;
  }

  ${theme.breakpoints.down('sm')} {
    width: 100%;
    height: auto;
  }

 
`



const BrochureContent = styled.div`
  
`

const BrochureWrapper = styled.div`
  margin: 0 auto;
  position: relative;
  padding: 38px 18px 18px;
  border-radius: 20px;
  border: 1px solid #DCDCDC;
  cursor: pointer;
  transition: all 200ms ease;
  height: 256px;

  picture {
    margin: auto;
  }

  ${theme.breakpoints.up('lg')} {
    picture {
      width: 150px;
      height: 106px;
      object-fit: contain;
      img {
        max-height: 106px;
      }
    }
  }

  ${theme.breakpoints.down('md')} {
    max-width: 100%;
    height: auto;

    picture {
      width: 60%;
    }
  }

  ${props => props.active === 1 && `
    
    background-color: white;
  `}

  ${props => props.active === 2 && `
    opacity: .5;
    cursor: auto;
    pointer-events: none;
  `}
`

const BrochureContainer = styled.div`;
  transition: opacity 200ms ease;
  opacity: 1;
  display: flex;
  flex-direction: column;
  gap: 23px;
`

const TextContent = styled.div `
  color: #0170D4;
  text-align: center;
  font-family: Open Sans;
  font-size: 24px;
  font-style: normal;
  font-weight: 800;
  line-height: 24px; 
  letter-spacing: 1px;
  text-transform: uppercase;

  ${theme.breakpoints.down('md')} {
    font-size: 18px;
    font-style: normal;
    font-weight: 800;
    line-height: 18px; 
  }
`

const Tick = styled.div`
  width: 27px;
  height: 27px;
  position: absolute;
  top: 8px;
  right: 8px;
  border-radius: 100%;
  border: 1px solid #CAC6C5;
  z-index: 1;

  svg {
    width: 25px;
    height: 25px;
    display: block;
    opacity: 0;
    transform: scale(0,0);
    transition: all 200ms ease;
  }

  ${props => props.display === 1 && `
    svg {
      opacity: 1;
      transform: scale(1, 1);
    }
  `}
`

const Ranges = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
`

const Range = styled.div`
  margin: 4px;

  button {
    border-color: #D6D6D6!important;
    border-width: 1px!important;
    background-color: white!important;
    text-transform: none!important;
    font-weight: 400!important;
    padding: 10px 15px!important;
    &:hover {
      background: none!important;
      filter: brightness(80%)!important;
    }
  }


  ${props => (props.active) && `
    button {
      color:${theme.palette.white}!important;
      background:${theme.palette.vividBlue}!important;
      font-weight:600!important;
      &:hover {
        color: white!important;
        filter: none!important;
        background:${theme.palette.vividBlue}!important;
      }
    }
  `}
`

const brochureRequestDefaultPropBrochures = [];

const BrochureRequest = (props) => {
  props = {
    ...props,
    brochures: typeof props.brochures === "undefined" ? brochureRequestDefaultPropBrochures : props.brochures
  };

  const [activeItems, updateActiveItems] = useState([]);
  const [filters, setFilters] = useState([]);

  const handleClick = (data) => {
    if (findIndex(activeItems, data) === -1) {
      // add
      const newState = produce(activeItems, draft => {
        draft.push(data);
      });
      updateActiveItems(newState)
    } else {
      // delete
      const newState = produce(activeItems, draft => {
        const index = findIndex(activeItems, data);
        draft.splice(index, 1);
      });
      updateActiveItems(newState)
    }
  }

  const activeItemsQs = qs.stringify({items: activeItems.map(item => {
    const origin = item.data.link.indexOf('http') === -1 ? window.location.origin : ''
    return {
      ...item.data,
      link: `${origin}${item.data.link}`
    }
  })});

  const nextUrl = `/brochure-request/form?${activeItemsQs}`;

  const handleFilter = (item) => {

    console.log('item...', item, filters.indexOf(item));
    if (filters.length === 0 || filters.indexOf(item) === -1) {
      setFilters([...filters,item]);
    } else {
      setFilters(filters.filter(e => e !== item));
    }
  }

  console.log('nextUrl', nextUrl);

  return (
    <Container>
      <TopBanner />
      <Section>
        <Grid container direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'}>
          <Grid size={12}>
            <Box px={2}>
              {props.heading &&
                <TitleContainer>
                  <Text variant="h1" align="center" uppercase colour="charcoal">{props.heading}</Text>
                  {props.subHeading && <Text variant="h2" align="center" uppercase colour="charcoal">{props.subHeading}</Text> }
                </TitleContainer>
              }
              {props.ranges &&
                <Text variant="subTitle" align="center" uppercase colour="charcoal">Filter By</Text>
              }

            </Box>
          </Grid>
        </Grid>
        {props.ranges &&
          <Ranges>
            {props.ranges.map((item, index) => {
              return (
                <Range key={index} active={filters.indexOf(item) !== -1 ? true : false} >
                  <Cta onClick={() => handleFilter(item)} secondary outline color={"#758094"}>{item}</Cta>
                </Range>
              )
            })}
          </Ranges>
        }
        <Brochures>

          {props.brochures.filter(item => (item.range && (filters.length === 0 || filters.indexOf(item.range) !== -1) || !props.ranges)).map((item, index) => (
    
              <BrochureItem
                onClick={() => handleClick(item)}
                key={item.title}
              >
                <BrochureWrapper active={findIndex(activeItems, item) === -1 ? activeItems.length > 2 ? 2 : 0 : 1}>
                  <Tick display={findIndex(activeItems, item) === -1 ? 0 : 1}>
                    <ReactSVG src={tickWhite} />
                  </Tick>

                  <BrochureContainer>

                    <RespImg 
                      {...item.image}
                    />

                    <BrochureContent>
                      {item.title &&
                        <Text variant="body1" alignItems={'flex-start'} colour="white">
                          <TextContent>{item.title}</TextContent>
                        </Text>
                      }
                    </BrochureContent>

                  </BrochureContainer>

                </BrochureWrapper>

              </BrochureItem>
  
          ))}
        </Brochures>
      </Section>
      <BannerPopup 
        open={activeItems.length > 0}
        cta={{
          title: 'Next',
          href: nextUrl
        }}
      />
    </Container>
  );
}

BrochureRequest.propTypes = {

}

export async function getStaticProps() {
  const countryCode = process.env.DEV_COUNTRY_CODE ? process.env.DEV_COUNTRY_CODE.toLowerCase() : process.env.COUNTRY_CODE.toLowerCase();
  const GET_BR_DATA = gql`
    query GET_BR_DATA {
      pageBy(uri: "request-a-brochure?${countryCode}") {
        title
        seo {
          ${seoQuery}
        }
        ${contentBuilderQuery()}
        acf_domains {
          assignedDomains
        }
        acf_brochure_list {
          blHeading
          blSubHeading
          blBrochuresRep {
            blBrochure {
              ... on PDFDocument {
                id
                uri
                title
                acf_pdf_document {
                  pdfDocumentFile {
                    guid
                  }
                  pdfDocThumbnailImage {
                    ${respImgQuery}
                  }
                }
              }
            }
            blRange {
              ... on Range {
                title
              }
            }
            blDescription
            blHeadline
            fieldGroupName
          }
        }
      }
    }
  `

  const result = await client.query({
    query: GET_BR_DATA,
  });

  const context = get(result, 'data.pageBy', {});
  
  const brochureData = restructorBrochures(context);

  const assignedDomains = get(result, 'data.pageBy.acf_domains.assignedDomains', []); 

  return {
    revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
    props: {
      meta: {
        title: context?.title ? context.title : null,
        seo: context?.seo ? context.seo : null,
        assignedDomains
      },
      breadcrumbs: await refactorBreadcrumbsData('/brochure-request', assignedDomains),
      // _result: result,
      ...brochureData,
    }
  }
}

export default BrochureRequest